import { TooltipTouchContext } from './tooltipInput';
import { Fragment, useContext, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { AppModelContext } from '@/models/AppModel';
import {
  keyCombinationDisplayParts,
  type KeyActions,
  type KeyCombination,
} from '@/models/KeyMapModel';

/** Keycaps share the same normalization and platform labels as Settings. */
export function ShortcutKeys({ combination }: { combination: KeyCombination }) {
  const parts = keyCombinationDisplayParts(combination);
  return (
    <span
      role="img"
      aria-label={`Keyboard shortcut: ${parts.join(' + ')}`}
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {parts.map((part, index) => (
        <Fragment key={`${index}-${part}`}>
          {index > 0 && <span aria-hidden="true">+</span>}
          <kbd
            aria-hidden="true"
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 20,
              minWidth: 20,
              padding: '0 4px',
              borderRadius: 4,
              background: 'color-mix(in srgb, currentColor 8%, transparent)',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              userSelect: 'none',
            }}
          >
            {part}
          </kbd>
        </Fragment>
      ))}
    </span>
  );
}

export const ShortcutHint = observer(function ShortcutHint({
  children,
  action,
  combination,
}: {
  children: ReactNode;
  action?: KeyActions;
  combination?: KeyCombination;
}) {
  const touchInput = useContext(TooltipTouchContext);
  const app = useContext(AppModelContext);
  const binding = action
    ? app?.userPreferencesModel.keyMapModel.getChar(action)
    : combination;
  return (
    <span css={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {children}
      {binding && !touchInput && <ShortcutKeys combination={binding} />}
    </span>
  );
});
